// src/utils/bookingCaseWriters.js
//
// Pass 50A §1–§9 (+inc4) — production-safe, atomic, TRULY idempotent, audited case writers.
//   · §1 fail-closed write gate
//   · §2/§6 deterministic ids + transaction-based create idempotency (retry = zero writes,
//        createdAt/audit untouched)
//   · §3 atomic batch/transaction (no partial success); §4 immutable audit staged in the same op
//   · §5 line edits sync active children via the ONE shared field derivation (incl startAt/endAt)
//   · §6(dec/inc) instructorsRequired increase/decrease; assigned excess REFUSED (zero writes)
//   · §7 canonical persisted slot cancel + restore; §8 stage transitions; §9 payment parent+child
// EMULATOR/test only — no production writes in this pass.

import { writeBatch, runTransaction, doc, collection, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { normalizeBooking, assignInstructorToBooking, unassignBooking } from './booking';
import { normalizePaymentState } from './paymentSemantics';
import {
  normalizeBookingCase, materializeCaseBookings, normalizeRequestLine, operationalFieldsFromRequestLine,
  bookingPaymentFromCasePayment, CASE_STAGE, caseShouldMaterializeOperationalSessions, resolveCaseTimeZone,
} from './bookingCase';
import { assertCanWriteBookingCases } from './operationalWriteGate';
// Accelerator F — the SHARED, pure id/document projections. The Node/firebase-admin partner
// persistence adapter imports the SAME helpers, so browser + admin writers cannot drift on booking
// document identity (`${caseId}__${requestSlotId}`), the case field projection, or the audit id.
import { bookingIdForCaseSlot, bookingCaseDocument, caseAuditId } from './bookingCasePersistence';

const REQS = PLATFORM_COLLECTIONS.BOOKING_REQUESTS;
const BOOKINGS = PLATFORM_COLLECTIONS.BOOKINGS;
const AUDIT = PLATFORM_COLLECTIONS.CASE_CHANGES;

let _seq = 0;
export function newCaseId() { return `case_${Date.now().toString(36)}_${(_seq++).toString(36)}_${Math.floor((typeof performance !== 'undefined' ? performance.now() : 0) % 1e6).toString(36)}`; }
const slotBookingId = bookingIdForCaseSlot; // shared deterministic id — never diverge from the admin adapter
const slotIndexOf = (requestSlotId) => Number(String(requestSlotId || '').split('#')[1]) || 0;

// Pass 51 defect #2 — write a deterministic child booking with CREATE-IF-ABSENT createdAt.
// Re-confirm / increase / re-materialise of an EXISTING child (its id already present) must NEVER
// rewrite the original createdAt: we merge fields + updatedAt only. A genuinely NEW child gets
// createdAt now. `existingIds` = Set of child booking ids that already exist for this case.
function writeChildBooking(batch, db, bid, fields, existingIds, actorUserId = '') {
  const ref = doc(db, BOOKINGS, bid);
  const { createdAt: _drop, ...rest } = fields; // never carry an incoming createdAt into the write
  if (existingIds.has(bid)) {
    batch.set(ref, { ...rest, updatedAt: serverTimestamp() }, { merge: true }); // preserve original createdAt
  } else {
    batch.set(ref, { ...rest, ...(actorUserId ? { createdBy: actorUserId } : {}), createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  }
}

// §9 case field projection — delegated to the SHARED pure projection so the admin adapter cannot drift.
const caseDocFields = bookingCaseDocument;

// §4 immutable audit event id keyed by operationId+type (create-once; never updated) — SHARED helper.
const auditId = caseAuditId;
function stageAudit(target, db, caseId, changeType, details, actor, operationId) {
  target.set(doc(db, AUDIT, auditId(operationId, changeType)), {
    caseId, changeType, details: details || {}, changedByUserId: actor?.actorUserId || '', changedByName: actor?.actorName || '',
    operationId, createdAt: serverTimestamp(),
  });
}

async function childBookingsOf(db, caseId) {
  const snap = await getDocs(query(collection(db, BOOKINGS), where('bookingRequestId', '==', caseId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// §2/§6 create a case + child slots as ONE transaction that is a TRUE no-op on retry: it reads the
// create-audit doc first; if the operation already committed, it writes nothing (createdAt + the
// immutable audit are untouched). Concurrency-safe.
// Pass 57C (items 9/10): New Booking may OPTIONALLY assign each instructor slot at creation. Slots stay
// canonical child bookings (one request line → N children, each independently assigned or unassigned);
// chosen slots are assigned through the AUTHORITATIVE assignInstructorToBooking contract after the case
// is created (never a separate assigned schema). `slotAssignments` = { '<lineId>#<slot>': { instructorId,
// instructorName } }; omitted/blank slots materialize as unassigned demand exactly as before.
export async function createBookingCase(db, input = {}, { actorUserId = '', actorName = '', operationId, userData = null, organizationId = '', slotAssignments = null } = {}) {
  // Pass 57A: authorize by actor role when a context is supplied (owner/admin); legacy flag otherwise.
  assertCanWriteBookingCases(userData ? { userData, organizationId: organizationId || input.organizationId, action: 'new_booking' } : null);
  const caseId = input.caseId || input.id || newCaseId();
  const opId = operationId || caseId;
  const caseObj = { ...normalizeBookingCase({ ...input, id: caseId, caseModelVersion: 1 }), id: caseId, caseModelVersion: 1 };
  if (!caseObj.organizationId) throw new Error('Organisation is required.');
  const payloads = materializeCaseBookings(caseObj, { existingBookings: [] });
  const bookingIds = payloads.map((p) => slotBookingId(caseId, p.requestSlotId));

  let created = true;
  await runTransaction(db, async (tx) => {
    const auditRef = doc(db, AUDIT, auditId(opId, 'case_created'));
    const prior = await tx.get(auditRef);
    if (prior.exists && prior.exists()) { created = false; return; } // §6 already committed → no-op
    tx.set(doc(db, REQS, caseId), { ...caseDocFields(caseObj), createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    for (const p of payloads) {
      const bid = slotBookingId(caseId, p.requestSlotId);
      tx.set(doc(db, BOOKINGS, bid), { ...normalizeBooking({ ...p, id: bid }), createdBy: actorUserId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    }
    tx.set(auditRef, { caseId, changeType: 'case_created', details: { stage: caseObj.stage, materialized: payloads.length }, changedByUserId: actorUserId, changedByName: actorName, operationId: opId, createdAt: serverTimestamp() });
  });

  // Pass 57C (items 9/10): assign any chosen slots through the authoritative assignment contract. Each
  // is the SAME canonical child booking, just assigned. Unchosen slots remain unassigned demand.
  const assigned = [];
  if (created && slotAssignments && typeof slotAssignments === 'object') {
    for (const p of payloads) {
      const a = slotAssignments[p.requestSlotId];
      if (!a || !a.instructorId) continue;
      const bid = slotBookingId(caseId, p.requestSlotId);
      // eslint-disable-next-line no-await-in-loop
      await assignInstructorToBooking(db, { bookingId: bid, instructorId: a.instructorId, instructorName: a.instructorName || '', userData, assignedByUserId: actorUserId, assignedByName: actorName });
      assigned.push(bid);
    }
  }
  return { caseId, bookingIds, materialized: payloads.length, assigned: assigned.length, created };
}

// §5/§6 upsert a line, SYNC active children (shared field derivation — start/end move with the
// line), and handle instructorsRequired inc/dec. An ASSIGNED excess slot REFUSES the decrease
// (§4) before any write. Atomic + audited. `resolution: 'cancel_assigned'` may be passed to
// explicitly cancel assigned excess in the same mutation.
export async function upsertCaseLine(db, caseObj, line, { actorUserId = '', operationId, resolution, userData = null, organizationId = '' } = {}) {
  assertCanWriteBookingCases({ userData, organizationId: organizationId || caseObj.organizationId, action: 'booking_case' });
  const ln = normalizeRequestLine(line);
  const existing = (await childBookingsOf(db, caseObj.id)).filter((b) => b.requestLineId === ln.lineId);
  const active = existing.filter((b) => b.status !== 'canceled');

  // §4 detect assigned excess slots (index > new required) — refuse unless explicitly resolved.
  const excessAssigned = active.filter((b) => slotIndexOf(b.requestSlotId) > ln.instructorsRequired && b.instructorId);
  if (excessAssigned.length && resolution !== 'cancel_assigned') {
    const who = excessAssigned.map((b) => `slot ${slotIndexOf(b.requestSlotId)} (${b.instructorNameSnapshot || b.instructorId})`).join(', ');
    const err = new Error(`Cannot reduce instructors: ${who} still assigned. Unassign/cancel first.`);
    err.code = 'ASSIGNED_EXCESS'; err.excess = excessAssigned.map((b) => b.requestSlotId);
    throw err; // zero writes
  }

  const opId = operationId || `${caseObj.id}:${ln.lineId}:${Date.now().toString(36)}`;
  const sessions = [...(caseObj.requestedSessions || [])];
  const idx = sessions.findIndex((s) => s.lineId === ln.lineId);
  // Pass 51 defect #1 — a partial line edit that does NOT explicitly supply cancelledSlots must
  // preserve the existing line's cancelledSlots; an unrelated edit (pickup/time/etc.) must never
  // resurrect a cancelled operational slot.
  if (idx >= 0 && line.cancelledSlots === undefined) {
    const prev = sessions[idx];
    if (prev && Array.isArray(prev.cancelledSlots) && prev.cancelledSlots.length) ln.cancelledSlots = [...prev.cancelledSlots];
  }
  if (idx >= 0) sessions[idx] = ln; else sessions.push(ln);

  const batch = writeBatch(db);
  batch.update(doc(db, REQS, caseObj.id), { requestedSessions: sessions, updatedAt: serverTimestamp() });

  // §5 propagate the shared operational fields onto active children (assignment/id/history kept).
  // Pass 82C.2 — a line edit re-derives start/end in the CASE's timezone so the child moves in org wall-clock.
  const opFields = operationalFieldsFromRequestLine(ln, { timeZone: resolveCaseTimeZone(caseObj) });
  for (const b of active) batch.update(doc(db, BOOKINGS, b.id), { ...opFields, updatedAt: serverTimestamp() });

  // §6 cancel excess slots beyond the new requirement (unassigned auto; assigned only if resolved).
  for (const b of active) {
    if (slotIndexOf(b.requestSlotId) > ln.instructorsRequired) {
      batch.update(doc(db, BOOKINGS, b.id), { status: 'canceled', cancellationResolution: 'slot_removed', updatedAt: serverTimestamp() });
    }
  }
  // §6 increase / initial fill: create missing slots (respecting canonical cancelledSlots + stage).
  const payloads = caseShouldMaterializeOperationalSessions(caseObj)
    ? materializeCaseBookings({ ...caseObj, requestedSessions: sessions }, { existingBookings: existing }).filter((p) => p.requestLineId === ln.lineId)
    : [];
  const createdIds = [];
  const existingIds = new Set(existing.map((b) => b.id));
  for (const p of payloads) { const bid = slotBookingId(caseObj.id, p.requestSlotId); writeChildBooking(batch, db, bid, normalizeBooking({ ...p, id: bid }), existingIds, actorUserId); createdIds.push(bid); }

  stageAudit(batch, db, caseObj.id, 'line_upserted', { lineId: ln.lineId, created: createdIds.length, cancelledExcess: excessAssigned.length ? 'assigned_resolved' : 'auto' }, { actorUserId }, opId);
  await batch.commit();
  return { created: createdIds };
}

// §2/§7 cancel ONE slot canonically: record the slot index on the PARENT line's cancelledSlots
// (so it survives edit/retry/re-materialisation) AND cancel the child booking. Case stays active.
export async function cancelCaseSlot(db, caseObj, requestSlotId, { actorUserId = '', reason = '', operationId, userData = null, organizationId = '' } = {}) {
  assertCanWriteBookingCases({ userData, organizationId: organizationId || caseObj.organizationId, action: 'booking_case' });
  const opId = operationId || `${caseObj.id}:${requestSlotId}:${Date.now().toString(36)}`;
  const [lineId] = String(requestSlotId).split('#');
  const slot = slotIndexOf(requestSlotId);
  const sessions = (caseObj.requestedSessions || []).map((s) => (s.lineId === lineId
    ? { ...s, cancelledSlots: [...new Set([...(s.cancelledSlots || []).map(Number), slot])] } : s));
  const child = (await childBookingsOf(db, caseObj.id)).find((b) => b.requestSlotId === requestSlotId && b.status !== 'canceled');
  const batch = writeBatch(db);
  batch.update(doc(db, REQS, caseObj.id), { requestedSessions: sessions, updatedAt: serverTimestamp() });
  if (child) batch.update(doc(db, BOOKINGS, child.id), { status: 'canceled', cancellationResolution: reason || 'slot_cancelled', updatedAt: serverTimestamp() });
  stageAudit(batch, db, caseObj.id, 'slot_cancelled', { requestSlotId, cancelled: !!child }, { actorUserId }, opId);
  await batch.commit();
  return { cancelled: !!child };
}

// §3 restore a cancelled slot: remove it from cancelledSlots and re-create the SAME deterministic
// child booking id (unassigned; history preserved via merge), audited. No new slot id.
export async function restoreCaseSlot(db, caseObj, requestSlotId, { actorUserId = '', operationId, userData = null, organizationId = '' } = {}) {
  assertCanWriteBookingCases({ userData, organizationId: organizationId || caseObj.organizationId, action: 'booking_case' });
  const opId = operationId || `${caseObj.id}:${requestSlotId}:restore:${Date.now().toString(36)}`;
  const [lineId] = String(requestSlotId).split('#');
  const slot = slotIndexOf(requestSlotId);
  const sessions = (caseObj.requestedSessions || []).map((s) => (s.lineId === lineId
    ? { ...s, cancelledSlots: (s.cancelledSlots || []).map(Number).filter((n) => n !== slot) } : s));
  const line = sessions.find((s) => s.lineId === lineId);
  const batch = writeBatch(db);
  batch.update(doc(db, REQS, caseObj.id), { requestedSessions: sessions, updatedAt: serverTimestamp() });
  if (line && caseShouldMaterializeOperationalSessions(caseObj)) {
    const caseTz = resolveCaseTimeZone(caseObj);
    const op = operationalFieldsFromRequestLine(line, { timeZone: caseTz });
    const bid = slotBookingId(caseObj.id, requestSlotId);
    batch.set(doc(db, BOOKINGS, bid), {
      ...normalizeBooking({ id: bid, organizationId: caseObj.organizationId, seasonId: caseObj.seasonId, bookingRequestId: caseObj.id, requestLineId: lineId, requestSlotId, requestSlotIndex: slot, instructorId: '', assignmentState: 'unassigned', status: 'confirmed', scheduleEntryType: 'client_booking', timezone: caseTz, ...op, requesterName: caseObj.requesterName, requesterEmail: caseObj.requesterEmail, paymentState: bookingPaymentFromCasePayment(caseObj.paymentState) }),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }
  stageAudit(batch, db, caseObj.id, 'slot_restored', { requestSlotId }, { actorUserId }, opId);
  await batch.commit();
  return { restored: requestSlotId };
}

// §7/§27 cancel a whole LINE (case stays active).
export async function cancelCaseLine(db, caseObj, lineId, { actorUserId = '', reason = '', operationId, userData = null, organizationId = '' } = {}) {
  assertCanWriteBookingCases({ userData, organizationId: organizationId || caseObj.organizationId, action: 'booking_case' });
  const opId = operationId || `${caseObj.id}:${lineId}:${Date.now().toString(36)}`;
  const sessions = (caseObj.requestedSessions || []).map((s) => (s.lineId === lineId ? { ...s, status: 'cancelled' } : s));
  const children = (await childBookingsOf(db, caseObj.id)).filter((b) => b.requestLineId === lineId && b.status !== 'canceled');
  const batch = writeBatch(db);
  batch.update(doc(db, REQS, caseObj.id), { requestedSessions: sessions, updatedAt: serverTimestamp() });
  for (const b of children) batch.update(doc(db, BOOKINGS, b.id), { status: 'canceled', cancellationResolution: reason || 'session_cancelled', updatedAt: serverTimestamp() });
  stageAudit(batch, db, caseObj.id, 'line_cancelled', { lineId, cancelledBookings: children.length }, { actorUserId }, opId);
  await batch.commit();
  return { cancelledBookings: children.length };
}

// §8 stage transitions with operational side-effects (atomic + audited).
export async function changeCaseStage(db, caseObj, nextStage, { actorUserId = '', actorName = '', operationId, userData = null, organizationId = '' } = {}) {
  // Pass 62 — production operator action MUST carry actor context through the canonical authorization boundary
  // (owner/admin/ops for the org); missing actor fails closed. NEVER a global build flag.
  assertCanWriteBookingCases({ userData, organizationId: organizationId || caseObj.organizationId, action: 'booking_case' });
  if (!Object.values(CASE_STAGE).includes(nextStage)) throw new Error(`Invalid stage: ${nextStage}`);
  if (caseObj.stage === CASE_STAGE.CONFIRMED && nextStage === CASE_STAGE.INQUIRY) {
    throw new Error('Cannot revert a confirmed case to inquiry while operational bookings exist.');
  }
  const opId = operationId || `${caseObj.id}:stage:${Date.now().toString(36)}`;
  const existing = await childBookingsOf(db, caseObj.id);
  const batch = writeBatch(db);
  batch.update(doc(db, REQS, caseObj.id), { stage: nextStage, updatedAt: serverTimestamp() });
  if (nextStage === CASE_STAGE.CONFIRMED) {
    const payloads = materializeCaseBookings({ ...caseObj, stage: CASE_STAGE.CONFIRMED }, { existingBookings: existing });
    const existingIds = new Set(existing.map((b) => b.id));
    for (const p of payloads) { const bid = slotBookingId(caseObj.id, p.requestSlotId); writeChildBooking(batch, db, bid, normalizeBooking({ ...p, id: bid }), existingIds, actorUserId); }
    stageAudit(batch, db, caseObj.id, 'stage_confirmed', { materialized: payloads.length }, { actorUserId }, opId);
  } else if (nextStage === CASE_STAGE.CANCELLED) {
    const activeCh = existing.filter((b) => b.status !== 'canceled');
    for (const b of activeCh) batch.update(doc(db, BOOKINGS, b.id), { status: 'canceled', cancellationResolution: 'case_cancelled', updatedAt: serverTimestamp() });
    stageAudit(batch, db, caseObj.id, 'stage_cancelled', { cancelledBookings: activeCh.length }, { actorUserId }, opId);
  } else {
    stageAudit(batch, db, caseObj.id, 'stage_changed', { stage: nextStage }, { actorUserId }, opId);
  }
  await batch.commit();
}

// Pass 62 — create ONLY the missing child bookings for an already-CONFIRMED case (operator "Create missing
// bookings" / repair). Reuses materializeCaseBookings + deterministic slot ids — NO alternate conversion
// logic, NO stage change, NO touch to existing (assigned) children, respects cancelled slots/lines. Idempotent:
// a second identical call creates 0 (existing slot ids are skipped). Returns the count created.
export async function materializeMissingCaseBookings(db, caseObj, { actorUserId = '', actorName = '', operationId, userData = null, organizationId = '' } = {}) {
  assertCanWriteBookingCases({ userData, organizationId: organizationId || caseObj.organizationId, action: 'booking_case' });
  if (caseObj.synthesized) throw new Error('A source-derived (read-only) case cannot be materialised.');
  if (caseObj.stage !== CASE_STAGE.CONFIRMED && caseObj.stage !== CASE_STAGE.COMPLETED) {
    throw new Error('Only a confirmed case materialises operational bookings.');
  }
  const opId = operationId || `${caseObj.id}:materialize:${Date.now().toString(36)}`;
  const existing = await childBookingsOf(db, caseObj.id);
  const existingIds = new Set(existing.map((b) => b.id));
  const payloads = materializeCaseBookings(caseObj, { existingBookings: existing }); // only the missing slots
  if (!payloads.length) return 0;
  const batch = writeBatch(db);
  let created = 0;
  for (const p of payloads) {
    const bid = slotBookingId(caseObj.id, p.requestSlotId);
    if (existingIds.has(bid)) continue;                       // idempotent — never rewrite an existing child
    writeChildBooking(batch, db, bid, normalizeBooking({ ...p, id: bid }), existingIds, actorUserId);
    created += 1;
  }
  if (!created) return 0;
  stageAudit(batch, db, caseObj.id, 'bookings_materialized', { created }, { actorUserId, actorName }, opId);
  await batch.commit();
  return created;
}

// §9 payment is ONE truth: update the case + denormalised active-child paymentState together.
export async function changeCasePayment(db, caseObj, paymentInput, { actorUserId = '', operationId, userData = null, organizationId = '' } = {}) {
  assertCanWriteBookingCases({ userData, organizationId: organizationId || caseObj.organizationId, action: 'booking_case' });
  const paymentState = normalizePaymentState(paymentInput);
  const opId = operationId || `${caseObj.id}:pay:${Date.now().toString(36)}`;
  const active = (await childBookingsOf(db, caseObj.id)).filter((b) => b.status !== 'canceled');
  const childPay = bookingPaymentFromCasePayment(paymentState); // §12 booking vocab for children
  const batch = writeBatch(db);
  batch.update(doc(db, REQS, caseObj.id), { paymentState, updatedAt: serverTimestamp() }); // case canonical PAYMENT
  for (const b of active) batch.update(doc(db, BOOKINGS, b.id), { paymentState: childPay, updatedAt: serverTimestamp() });
  stageAudit(batch, db, caseObj.id, 'payment_changed', { paymentState, children: active.length }, { actorUserId }, opId);
  await batch.commit();
}

export async function addCaseCommunication(db, caseObj, comm, { actorUserId = '', operationId, userData = null, organizationId = '' } = {}) {
  assertCanWriteBookingCases({ userData, organizationId: organizationId || caseObj.organizationId, action: 'booking_case' });
  const opId = operationId || `${caseObj.id}:comm:${Date.now().toString(36)}`;
  const communications = [...(caseObj.communications || []), comm];
  const batch = writeBatch(db);
  batch.update(doc(db, REQS, caseObj.id), { communications, updatedAt: serverTimestamp() });
  stageAudit(batch, db, caseObj.id, 'communication_added', { channel: comm.channel }, { actorUserId }, opId);
  await batch.commit();
}

// §13 case assignment goes through the canonical capability-gated booking writers, but the CASE
// wrappers ALSO enforce the 50A write gate (so case-detail/wizard assign is blocked in production
// pre-activation). The underlying shared scheduling writers are unchanged.
// Pass 62 — thread actor context to the case-write gate; assignInstructorToBooking/unassignBooking remain the
// authoritative assignment writers with their own authorization.
export async function assignCaseSlot(db, args = {}) { assertCanWriteBookingCases({ userData: args.userData, organizationId: args.organizationId, action: 'assignment' }); return assignInstructorToBooking(db, args); }
export async function unassignCaseSlot(db, args = {}) { assertCanWriteBookingCases({ userData: args.userData, organizationId: args.organizationId, action: 'assignment' }); return unassignBooking(db, args); }
